<script lang="ts">
  import { _ } from 'svelte-i18n';
  import {
    Building2,
    UserRound,
    Radar,
    FileSpreadsheet,
    Bookmark,
    Compass,
    Users,
    ArrowRight,
    Download,
    Loader2
  } from 'lucide-svelte';

  import { type Door } from '$lib/stores/guided';
  import { showDoor, showExportHistory, showList } from '$lib/stores/navigate';
  import {
    downloadExport,
    fetchExportHistory,
    ExportRefused,
    type PastExport
  } from '$lib/api/accountorder';
  import { account } from '$lib/stores/auth';
  import { notifications } from '$lib/stores/notifications';
  import { savedLists } from '$lib/stores/savedlists';

  /**
   * Where a signed-in customer lands.
   *
   * # It is a dashboard, not a search screen
   *
   * It held a large search box, and the box has moved to the top bar. Two
   * controls asking the same question is how this app came to feel like a pile
   * of modes: the box was reachable only from here, so "look this company up"
   * meant first navigating to the screen that could navigate.
   *
   * With lookup available everywhere, the front screen is free to answer the
   * question somebody actually arrives with — *where was I, and what do I want
   * to start?* So: the work they already have, and the ways to begin more.
   *
   * # The two things a member does
   *
   * Coming back to a list they are building, and starting a new one. Those are
   * the two blocks below, in that order, because a returning customer far more
   * often wants the first.
   */

  const doors: Array<{ id: Door; icon: typeof Building2 }> = [
    { id: 'industry', icon: Building2 },
    { id: 'role', icon: UserRound },
    { id: 'similarity', icon: Radar },
    { id: 'list', icon: FileSpreadsheet }
  ];

  const greeting = $derived(
    $account?.full_name?.trim()?.split(' ')[0] ?? $account?.company?.trim() ?? ''
  );

  /**
   * The lists worth showing, most recently touched first.
   *
   * Five, because this is a way back to work in progress rather than a
   * catalogue — the sidebar holds all of them.
   */
  const recent = $derived(
    [...$savedLists].sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)).slice(0, 5)
  );

  /**
   * The last few exports, for the block below.
   *
   * Loaded here rather than taken from a store: the dashboard is the only
   * screen that shows them in summary, and a store would be a cache with one
   * reader. The full list has its own screen.
   */
  let exports = $state<PastExport[]>([]);
  let downloading = $state(0);

  $effect(() => {
    void fetchExportHistory()
      .then((found) => {
        exports = found.slice(0, 3);
      })
      // Silent: the dashboard is not the screen to report this on, and the
      // block simply does not appear. The history screen says what went wrong.
      .catch(() => {});
  });

  async function take(order: PastExport) {
    downloading = order.order_id;
    try {
      await downloadExport(order.order_id, order.invoice_number || `export-${order.order_id}`);
    } catch (error) {
      notifications.error(
        error instanceof ExportRefused ? error.message : $_('export.history.downloadFailed')
      );
    } finally {
      downloading = 0;
    }
  }

  const money = (order: PastExport) =>
    `${order.currency} ${order.gross.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const day = (iso: string) => {
    const at = new Date(iso);
    return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('de-CH');
  };

  async function show(id: string) {
    try {
      await showList(id);
    } catch {
      notifications.error($_('savedLists.nav.openFailed'));
    }
  }

  /** Roughly how long ago a list was touched, for a label beside it. */
  function whenTouched(iso: string): string {
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return '';

    const days = Math.floor((Date.now() - then) / 86_400_000);
    if (days <= 0) return $_('home.recent.today');
    if (days === 1) return $_('home.recent.yesterday');
    return $_('home.recent.daysAgo', { values: { days } });
  }
</script>

<div class="flex h-full flex-col items-center overflow-y-auto px-4 py-10 sm:px-6">
  <!-- The same width as a step screen. Narrower than that left the doors —
       three cards in a row — squeezed into a column of prose. -->
  <div class="w-full max-w-5xl">
    <!--
      The greeting, and nothing under it.

      A line of prose stood here describing what the page contains — the lists,
      the ways to start one, and where the search is. Every one of those is
      visible on the page itself, under its own heading, so the sentence said
      out loud what the layout was already saying and pushed the first real
      thing further down.
    -->
    <h1 class="mb-8 text-2xl font-semibold tracking-tight text-ink-800">
      {greeting ? $_('home.greetingNamed', { values: { name: greeting } }) : $_('home.greeting')}
    </h1>

    <!--
      Each block's heading has moved inside the block it names.

      They were small grey labels floating on the canvas above white boxes,
      which is three loose parts per block — a label, a gap, a card — and no
      two of them looked like they belonged to each other. As a header band
      the heading is attached to what it heads, and it is the one strip of the
      card where a little colour costs nothing: see `.card-header-brand`.
    -->
    {#if recent.length > 0}
      <section class="card mb-8 overflow-hidden">
        <div class="card-header-brand">
          <span class="card-mark"><Bookmark class="h-4 w-4" /></span>
          <h2 class="card-title">{$_('home.recent.heading')}</h2>
        </div>

        <ul class="divide-y divide-hairline">
          {#each recent as list (list.id)}
            <li>
              <button
                type="button"
                onclick={() => show(list.id)}
                class="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-canvas"
              >
                <!--
                  Neutral until pointed at.

                  Five of these in a column, each in the accent, put more
                  purple on the dashboard than the whole rest of the app has —
                  and none of it meant anything, since the icon shape is what
                  distinguishes a shared list from a personal one. The header's
                  mark carries the colour for the block; the row earns it on
                  hover, where it is saying "this is what you would open".
                -->
                <span
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-surface bg-surface-sunken text-ink-400 transition-colors group-hover:bg-accent-600/10 group-hover:text-accent-700"
                >
                  {#if list.scope === 'organisation'}
                    <Users class="h-4 w-4" />
                  {:else}
                    <Bookmark class="h-4 w-4" />
                  {/if}
                </span>
                <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                  {list.name}
                </span>
                <span class="shrink-0 text-xs tabular-nums text-gray-500">
                  {$_('savedLists.view.entries', { values: { count: list.entries } })}
                </span>
                <span class="hidden shrink-0 text-xs text-gray-400 sm:inline">
                  {whenTouched(list.updated_at)}
                </span>
                <ArrowRight
                  class="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-accent-600"
                />
              </button>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <!--
      What has been exported, and the way to fetch it again.

      Three, with a link to the rest: this is a reminder that the files are
      there and stay current, not the place to work through them.
    -->
    {#if exports.length > 0}
      <section class="card mb-8 overflow-hidden">
        <div class="card-header-brand">
          <span class="card-mark"><FileSpreadsheet class="h-4 w-4" /></span>
          <h2 class="card-title">{$_('home.exported.heading')}</h2>
          <!-- The way to the rest, at the far end of the band it belongs to. -->
          <button
            type="button"
            onclick={showExportHistory}
            class="ml-auto text-xs font-medium text-accent-700 hover:underline"
          >
            {$_('home.exported.all')}
          </button>
        </div>

        <ul class="divide-y divide-hairline">
          {#each exports as order (order.order_id)}
            <li class="flex items-center gap-3 px-4 py-3">
              <span
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-surface bg-surface-sunken text-ink-400"
              >
                <FileSpreadsheet class="h-4 w-4" />
              </span>

              <div class="min-w-0 flex-1">
                <p class="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                  {$_('export.rows', { values: { count: order.rows } })}

                  <!--
                    Beside what it describes rather than at the end of the row.

                    It sat in the last column, where the download button is, so
                    it read as the action for that row — a thing to click — and
                    it was the width of two words away from the export it was
                    about. Here it qualifies the export, which is what it does.
                  -->
                  {#if !order.settled}
                    <span
                      class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"
                    >
                      {$_('export.history.awaitingPayment')}
                    </span>
                  {/if}
                </p>
                <p class="text-xs text-gray-500">{day(order.placed_at)}</p>
              </div>

              <span class="shrink-0 text-sm tabular-nums text-gray-700">
                {#if order.priced}
                  {money(order)}
                {:else}
                  <span class="text-xs text-gray-400">{$_('export.history.noAmount')}</span>
                {/if}
              </span>

              <!-- Only a settled export can be fetched; an unpaid prepaid one
                   is not the customer's to take yet. -->
              {#if order.settled}
                <button
                  type="button"
                  onclick={() => take(order)}
                  disabled={downloading === order.order_id}
                  title={$_('export.history.download')}
                  class="shrink-0 rounded-control p-1.5 text-gray-500 transition-colors hover:bg-surface-canvas hover:text-accent-700 disabled:opacity-50"
                >
                  <span class="sr-only">{$_('export.history.download')}</span>
                  {#if downloading === order.order_id}
                    <Loader2 class="h-4 w-4 animate-spin" />
                  {:else}
                    <Download class="h-4 w-4" />
                  {/if}
                </button>
              {/if}
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <section class="card overflow-hidden">
      <div class="card-header-brand">
        <span class="card-mark"><Compass class="h-4 w-4" /></span>
        <h2 class="card-title">{$_('home.doorsHeading')}</h2>
      </div>
      <!--
        The ways to begin. They are the guide's doors, unchanged — this screen
        is a way in to them rather than an alternative to them.

        A fifth is coming: the offering search, "nach Angebot", which is the
        other half of splitting the box in the bar from the box that builds a
        set. See docs/refactor/find-and-build.md.
      -->
      <div class="card-body grid grid-cols-2 gap-3 sm:grid-cols-4">
        {#each doors as door (door.id)}
          <button
            type="button"
            onclick={() => showDoor(door.id)}
            class="group flex flex-col items-center gap-2 rounded-surface border border-hairline bg-surface-canvas px-3 py-4 text-center transition-colors hover:border-accent-400 hover:bg-accent-600/[0.04]"
          >
            <span
              class="flex h-9 w-9 items-center justify-center rounded-surface bg-surface-sunken text-ink-400 transition-colors group-hover:bg-accent-600/10 group-hover:text-accent-700"
            >
              <door.icon class="h-4 w-4" />
            </span>
            <span class="text-xs font-medium leading-tight text-ink-800">
              {$_(`guided.door.${door.id}Title`)}
            </span>
          </button>
        {/each}
      </div>
    </section>
  </div>
</div>
